import ActionButton from 'components/common/ActionButton';
import FalconComponentCard from 'components/common/FalconComponentCard';
import PageHeader from 'components/common/PageHeader';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormLabel,
  InputGroup,
  Offcanvas,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';

import {
  faFileContract,
  faPlusCircle,
  faStar,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import corner3 from 'assets/img/illustrations/corner-3.png';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import SimpleBarReact from 'simplebar-react';
import { toast } from 'react-toastify';
import { showAlert, showConfirm } from 'stores/modal';
import api from 'util/api';
import logger from 'util/logger';
import DocumentTypesForm from './DocumentTypesForm';
import segmentHelper from 'helpers/segment-helper';
import draftEntryUiHelper from 'helpers/draft-entry-ui-helper';
import { useAsyncDebounce } from 'react-table';
import { useEnumerateDocTypesQuery } from 'rtk/docType.slice';
import Background from 'components/common/Background';

const DocumentTypes = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypesDisplay, setDocumentTypesDisplay] = useState([]);

  const [sortBy, setSortBy] = useState('Created');
  const [isAsc, setIsAsc] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [endOfResults, setEndOfResults] = useState(false);

  const [showDocumentTypeForm, setShowDocumentTypesForm] = useState(false);
  const [formTitle, setFormTitle] = useState(null);

  const [toggleFavorite, setToggleFavorite] = useState(false);

  useForm();

  const dispatch = useDispatch();

  const skip = pageIndex * pageSize;
  const {
    data: enumerateRes,
    isFetching,
    isLoading,
    isError: documentTypeLoadError,
    error: enumerateError,
    refetch
  } = useEnumerateDocTypesQuery({
    skip,
    keyword: searchKeyword,
    sortKey: sortBy,
    sortOrderAsc: isAsc,
    pageSize
  });

  const loading = isFetching || isLoading;

  const onDocumentTypeAddedSuccess = () => {
    refetch();
    setShowDocumentTypesForm(false);
  };

  const onDocumentTypeAddedFailure = () => {
    refetch();
  };

  const updateDocumentType = e => {
    setToggleFavorite(e.GUID);
    let data = _.cloneDeep(e);
    data.IsFavorite = !e.IsFavorite;
    api.DocumentType.update(e.GUID, data)
      .then(() => {
        setToggleFavorite(false);
        refetch();
      })
      .catch(() => {
        setToggleFavorite(false);
      });
  };

  const deleteDocumentType = DocumentType => {
    dispatch(
      showConfirm(
        'Are you sure?',
        'Are you sure you want to remove this  Document Type?',
        res => {
          if (res) {
            api.DocumentType.remove(DocumentType.GUID)
              .then(() => {
                segmentHelper.track(segmentHelper.events.DOCUMENT_TYPE_DELETED);
                toast.success(`Document Type removed successfully`, {
                  theme: 'colored',
                  position: 'bottom-center',
                  icon: faUserCheck,
                  toastId: 'login-success'
                });
                refetch();
              })
              .catch(() => {
                showAlert(
                  'Error Deleting  documentType',
                  'There was a problem removing this l Document Type.  Please try again.'
                );
                refetch();
              });
          }
        }
      )
    );
  };

  const onSearchKeywordChange = useAsyncDebounce(keyword => {
    setSearchKeyword(keyword);
    setPageIndex(0);
  }, 400);

  const addNewDocumentType = () => {
    setFormTitle('Add new Document Type');
    setShowDocumentTypesForm(true);
  };

  useEffect(() => {
    if (documentTypeLoadError) {
      logger.log('error loading documentTypes', enumerateError);
    }
  }, [documentTypeLoadError, enumerateError]);

  useEffect(() => {
    const list = enumerateRes?.Objects || [];
    setDocumentTypes(list);
    setDocumentTypesDisplay(list);
    setTotalRecords(enumerateRes?.TotalRecords ?? 0);
    setEndOfResults(!!enumerateRes?.EndOfResults);
  }, [enumerateRes]);

  useEffect(() => {
    setPageIndex(0);
    setEndOfResults(false);
  }, [pageSize]);

  useEffect(() => {
    refetch();
  }, []);

  return (
    <>
      {/* <PageHeader
        preTitle="Document Types"
        title=" Document Type list"
        description=""
        className="mb-3"
        titleTag="h2"
        image={corner3}
      /> */}

      <Row className="mb-3 g-3">
        <Col lg={12}>
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4'}
              title="Document Types"
              subtitle="Browse all document types"
              endEl={
                <Flex className={'g-2'}>
                  <div>
                    <Form as={Row} className="gx-2 d-flex align-items-center">
                      <Col xs="auto">
                        <FormLabel className="mb-0">Sort by:</FormLabel>
                      </Col>
                      <Col xs="auto">
                        <InputGroup>
                          <Form.Select
                            size="sm"
                            className="pe-5"
                            value={sortBy}
                            onChange={({ target }) => {
                              setSortBy(target.value);
                              setPageIndex(0);
                            }}
                          >
                            {/* <option value="NotarizationType">Type</option> */}
                            {/* <option value="Name">Name</option> */}
                            <option value="Created">Created</option>
                            <option value="IsFavorite">Favorite</option>
                          </Form.Select>
                          <InputGroup.Text
                            as={Button}
                            variant="link"
                            className="border border-300 text-700"
                            onClick={() => {
                              const nextAsc = !isAsc;
                              setIsAsc(nextAsc);
                              setPageIndex(0);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                isAsc ? 'sort-amount-up' : 'sort-amount-down'
                              }
                            />
                          </InputGroup.Text>
                        </InputGroup>
                      </Col>
                    </Form>
                  </div>
                  <div className="mx-1">
                    <Form.Control
                      onChange={e => onSearchKeywordChange(e.target.value)}
                      placeholder="Search document..."
                      name="search"
                    ></Form.Control>
                  </div>
                  <div className="mx-1">
                    <Button
                      variant="primary"
                      onClick={() => addNewDocumentType()}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} size="sm" /> Add New
                    </Button>
                  </div>
                </Flex>
              }
            />
            <FalconComponentCard.Body noLight className="py-0 px-4">
              {isLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {documentTypes.length > 0 && !isLoading && (
                <Pagination
                  onRefresh={() => refetch()}
                  loading={loading}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  currentCount={documentTypes.length}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  endOfResults={endOfResults}
                  disabled={loading}
                />
              )}
              {documentTypesDisplay.length !== 0 &&
                !documentTypeLoadError &&
                !isLoading && (
                  <SimpleBarReact>
                    <Table
                      striped
                      bordered
                      className={loading ? 'refreshing-skeleton' : undefined}
                    >
                      <colgroup>
                        <col />
                        <col />
                        <col className="d-sm-none d-md-block" />
                        <col />
                      </colgroup>
                      <thead>
                        <tr>
                          <th scope="col">Favorite</th>
                          <th scope="col">Notarization Type</th>
                          <th scope="col">Document Title</th>
                          <th scope="col">Created</th>
                          <th className="text-end" scope="col">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {_.map(documentTypesDisplay, e => {
                          let createdDate = draftEntryUiHelper
                            .getDateTimeWithTimeZone(e.Created)
                            .format('l, LT z');

                          return (
                            <tr key={e.GUID}>
                              <td className="cursor-pointer">
                                {toggleFavorite === e.GUID ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <FontAwesomeIcon
                                    className={
                                      e.IsFavorite ? 'text-primary' : ''
                                    }
                                    icon={faStar}
                                    onClick={() => updateDocumentType(e)}
                                  />
                                )}
                              </td>
                              <td>{e.NotarizationType}</td>
                              <td>{e.Value}</td>
                              <td>{createdDate}</td>
                              <td className="text-end">
                                <ActionButton
                                  icon="trash-alt"
                                  title="Remove"
                                  label=""
                                  variant="action"
                                  className="p-0 text-danger"
                                  onClick={() => deleteDocumentType(e)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </SimpleBarReact>
                )}
              {documentTypes.length === 0 &&
                !loading &&
                (documentTypeLoadError ? (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faFileContract} size="3x" />
                    <br />
                    <p className="my-3">
                      Unable to load document type list. Try again
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faFileContract} size="3x" />
                    <br />
                    <p className="my-3">
                      You haven't created any document types yet. <br />
                      Click "Add new" to create a new document type
                    </p>
                  </div>
                ))}
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      <Offcanvas
        id="offcanvas-address-form"
        show={showDocumentTypeForm}
        backdrop="static"
        onHide={() => setShowDocumentTypesForm(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{formTitle}</Offcanvas.Title>
        </Offcanvas.Header>
        <hr className="my-0" />
        <Offcanvas.Body>
          <DocumentTypesForm
            setShowDocumentTypesForm={setShowDocumentTypesForm}
            onSuccess={onDocumentTypeAddedSuccess}
            onFailure={onDocumentTypeAddedFailure}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default DocumentTypes;
